import React from 'react';
import { Link } from 'react-router-dom';
import Spline from '@splinetool/react-spline';
import { ArrowRight, Cpu, Leaf, TrendingUp, Users } from 'lucide-react';
import { caseStudies } from '../data/mock';

const Home = () => {
  const services = [
    {
      title: 'AI Tools & Agents',
      description: 'Bespoke AI systems that automate workflows, extract insights, and integrate with your existing tools.',
      benefits: [
        'Autonomous agents for complex workflows',
        'LLM-powered data extraction & analysis',
        'Custom integrations with APIs & databases'
      ],
      link: '/ai-tools'
    },
    {
      title: 'HFC & CO₂ Reduction Strategy',
      description: 'Expert climate consultancy powered by AI modeling to achieve compliance and reduce emissions.',
      benefits: [
        'Kigali Amendment & Paris Agreement compliance',
        'AI-driven scenario modeling & forecasting',
        'Executive dashboards & reporting'
      ],
      link: '/climate-strategy'
    }
  ];

  const whyFutura = [
    {
      icon: <Leaf size={32} />,
      title: 'Deep Climate Expertise',
      description: 'Our team has advised governments on emissions policy and delivered measurable CO₂ reductions.'
    },
    {
      icon: <Cpu size={32} />,
      title: 'Production-Grade AI',
      description: 'We build robust, scalable AI systems that work reliably in enterprise environments.'
    },
    {
      icon: <TrendingUp size={32} />,
      title: 'End-to-End Delivery',
      description: 'From discovery workshops to implementation and ongoing support—we deliver complete solutions.'
    },
    {
      icon: <Users size={32} />,
      title: 'Global Treaty Compliance',
      description: 'Expert knowledge of EU Green Deal, Kigali Amendment, and national climate regulations.'
    }
  ];

  const outcomes = [
    { metric: '42%', label: 'Average cost reduction in AI projects' },
    { metric: '65%', label: 'Projected HFC reduction in compliance programs' },
    { metric: '€8M+', label: 'Climate opportunities identified for clients' },
    { metric: '150+', label: 'Stakeholders managed in policy programs' }
  ];

  const processSteps = [
    { number: '01', title: 'Discovery', description: 'We understand your challenges, goals, and constraints through workshops and stakeholder interviews.' },
    { number: '02', title: 'Design & Modeling', description: 'We design AI architectures or climate roadmaps, model scenarios, and validate approaches.' },
    { number: '03', title: 'Implementation', description: 'We build, test, and deploy solutions with rigorous quality checks and integration support.' },
    { number: '04', title: 'Monitoring', description: 'Ongoing optimization, reporting, and support to ensure sustained impact and ROI.' }
  ];

  return (
    <div style={{ paddingTop: '80px', background: 'var(--bg-primary)' }}>
      {/* Hero Section with Spline */}
      <section style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        padding: '60px 7.6923%',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '60px',
          alignItems: 'center',
          width: '100%'
        }} className="hero-grid">
          {/* Left: Text Content */}
          <div>
            <h1 className="display-huge" style={{ marginBottom: '24px' }}>
              AI systems for a decarbonised future
            </h1>
            <p className="body-large" style={{ marginBottom: '40px', color: 'var(--text-secondary)' }}>
              We design and build autonomous AI tools that streamline operations—and apply that same technology to solve climate challenges like HFC phase-down and CO₂ reduction.
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn-primary">
                Book a strategy call
                <ArrowRight size={20} />
              </Link>
              <Link to="/ai-tools" className="btn-secondary">
                Explore AI solutions
              </Link>
            </div>
          </div>

          {/* Right: Spline 3D Animation */}
          <div style={{ 
            width: '100%', 
            height: '700px', 
            overflow: 'visible', 
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Spline scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode" />
          </div>
        </div>

        <style>{`
          @media (max-width: 1024px) {
            .hero-grid {
              grid-template-columns: 1fr !important;
              gap: 40px !important;
            }
          }
        `}</style>
      </section>

      {/* What We Do */}
      <section style={{ padding: '100px 7.6923%', background: 'var(--bg-primary)' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <h2 className="display-medium" style={{ marginBottom: '60px', textAlign: 'center' }}>
            What We Do
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
            gap: '60px'
          }}>
            {services.map((service, idx) => (
              <div
                key={idx}
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-subtle)',
                  padding: '40px',
                  transition: 'all 0.4s ease'
                }}
                className="dark-hover"
              >
                <h3 className="heading-1" style={{ marginBottom: '16px' }}>
                  {service.title}
                </h3>
                <p className="body-medium" style={{ marginBottom: '24px' }}>
                  {service.description}
                </p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, marginBottom: '32px' }}>
                  {service.benefits.map((benefit, i) => (
                    <li key={i} style={{ 
                      fontSize: '16px', 
                      color: 'var(--text-secondary)',
                      marginBottom: '12px',
                      paddingLeft: '24px',
                      position: 'relative'
                    }}>
                      <span style={{
                        position: 'absolute',
                        left: 0,
                        color: 'var(--brand-primary)'
                      }}>→</span>
                      {benefit}
                    </li>
                  ))}
                </ul>
                <Link to={service.link} style={{
                  color: 'var(--brand-primary)',
                  textDecoration: 'none',
                  fontSize: '18px',
                  fontWeight: 500,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'gap 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.gap = '12px'}
                onMouseLeave={(e) => e.currentTarget.style.gap = '8px'}
                >
                  Learn more <ArrowRight size={20} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Futura */}
      <section style={{ padding: '100px 7.6923%', background: 'var(--bg-primary)' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <h2 className="display-medium" style={{ marginBottom: '60px', textAlign: 'center' }}>
            Why Futura
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '40px'
          }}>
            {whyFutura.map((item, idx) => (
              <div key={idx} style={{ textAlign: 'center' }}>
                <div style={{ 
                  color: 'var(--brand-primary)', 
                  marginBottom: '16px',
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  {item.icon}
                </div>
                <h3 className="heading-3" style={{ marginBottom: '12px' }}>
                  {item.title}
                </h3>
                <p className="body-small">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Outcomes */}
      <section style={{ padding: '100px 7.6923%', background: 'var(--bg-secondary)' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', textAlign: 'center' }}>
          <h2 className="display-medium" style={{ marginBottom: '60px' }}>
            Sample Outcomes
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '40px'
          }}>
            {outcomes.map((outcome, idx) => (
              <div key={idx}>
                <div className="display-large" style={{ color: 'var(--brand-primary)', marginBottom: '8px' }}>
                  {outcome.metric}
                </div>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  {outcome.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Highlights */}
      <section style={{ padding: '100px 7.6923%', background: 'var(--bg-primary)' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <h2 className="display-medium" style={{ marginBottom: '60px', textAlign: 'center' }}>
            Case Highlights
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '40px'
          }}>
            {caseStudies.map((study) => (
              <Link
                key={study.id}
                to={`/case-studies/${study.slug}`}
                style={{ textDecoration: 'none' }}
              >
                <div
                  style={{
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border-subtle)',
                    overflow: 'hidden',
                    transition: 'all 0.4s ease'
                  }}
                  className="dark-hover"
                >
                  <img
                    src={study.image}
                    alt={study.title}
                    style={{
                      width: '100%',
                      height: '200px',
                      objectFit: 'cover'
                    }}
                  />
                  <div style={{ padding: '32px' }}>
                    <div style={{ 
                      fontSize: '14px', 
                      color: 'var(--brand-primary)', 
                      marginBottom: '12px',
                      fontWeight: 500
                    }}>
                      {study.serviceType}
                    </div>
                    <h3 className="heading-3" style={{ marginBottom: '12px', color: 'var(--text-primary)' }}>
                      {study.title}
                    </h3>
                    <p className="body-small" style={{ marginBottom: '16px' }}>
                      {study.challenge.substring(0, 120)}...
                    </p>
                    <div style={{
                      color: 'var(--brand-primary)',
                      fontSize: '16px',
                      fontWeight: 500,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      Read case study <ArrowRight size={18} />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '60px' }}>
            <Link to="/case-studies" className="btn-secondary">
              View all case studies
            </Link>
          </div>
        </div>
      </section>

      {/* Process / Next Steps */}
      <section style={{ padding: '100px 7.6923%', background: 'var(--bg-secondary)' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <h2 className="display-medium" style={{ marginBottom: '60px', textAlign: 'center' }}>
            Our Process
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '40px'
          }}>
            {processSteps.map((step, idx) => (
              <div key={idx}>
                <div style={{ 
                  fontSize: '48px', 
                  fontWeight: 600, 
                  color: 'var(--brand-primary)',
                  marginBottom: '16px'
                }}>
                  {step.number}
                </div>
                <h3 className="heading-3" style={{ marginBottom: '12px' }}>
                  {step.title}
                </h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ padding: '100px 7.6923%', background: 'var(--bg-primary)', textAlign: 'center' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 className="display-large" style={{ marginBottom: '24px' }}>
            Ready to get started?
          </h2>
          <p className="body-large" style={{ marginBottom: '40px', color: 'var(--text-secondary)' }}>
            Book a 30-minute discovery call to explore how AI and climate strategy can transform your organization.
          </p>
          <Link to="/contact" className="btn-primary">
            Book your call now
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
